export interface EventItemGroup {
  id?: string | null;
  type?: string | null;
  status_code?: string | null;
  carrier_id?: string | null;
  carrier_slug?: string | null;
  partner_carrier_id?: string | null;
  dzip?: string | null;
  access_point?: boolean | null;
  redirect_to_hold?: boolean | null;
  display?: boolean | null;
  items?: {
    item_id?: string | null;
    quantity?: number | null;
    unit_price?: number | null;
  }[];
}

export interface Config {
  name?: string | null;
  options?: string[] | null;
}

export enum NoflakeTag {
  TRACK_FE = 'noflake.track_fe',
  COMPONENT_PLATFORM = 'noflake.component_platform',
  NARVAR_JS = 'noflake.narvar_js',
  BAD = 'BAD',
  RETURNS = 'noflake.returns',
  HIPAA_RETURNS = 'noflake.hipaa_returns',
  SHOPIFY_RETURNS = 'noflake.shopify_returns',
  GEN_AI_RESPONSE_EVENTS = 'noflake.gen_ai_response_events',
  ASSIST_FE = 'noflake.assist_fe',
}

export interface AnalyticsPayloadRequired {
  clientDeviceTimeOffsetMinsToUtc: string;
  eventSchema: string;
  eventTs: string;
  eventType: string;
  guid: string;
  pageRetailerMoniker: string;
  pageTitle: string;
  pageUrl: string;
  productName: string;
  tag: NoflakeTag;
}

export interface AnalyticsPayloadNullable {
  bannerSegmentId?: string | null;
  bannerSegmentName?: string | null;
  chatClientSessionId?: string | null;
  clickAssetId?: string | null;
  clickAssetMultilinkId?: string | null;
  clickAssetSchdEndDateUtc?: string | null;
  clickAssetSchdStartDateUtc?: string | null;
  clickAssetType?: string | null;
  clickAssetUrl?: string | null;
  clickCategory?: string | null;
  clickDeliveryInstructionsComment?: string | null;
  clickDeliveryInstructionsLocation?: string | null;
  clickLinkDestinationUrl?: string | null;
  clickLinkLabel?: string | null;
  clickLocationId?: string | null;
  clickNotificationIsChangeNumber?: boolean | null;
  clickNotificationIsOptIn?: boolean | null;
  clickNotificationIsOptOut?: boolean | null;
  clickNotificationMarketingOptInChecked?: boolean | null;
  clickNotificationOptInDisclaimerText?: string | null;
  clickNotificationMarketingPrivacyLink?: string | null;
  clickNotificationMarketingTermsLink?: string | null;
  clickNotificationMarketingOptInText?: string | null;
  clickNotificationIsOrderLevelSignup?: boolean | null;
  clickProductRecItemData?: string | null;
  clickProductRecItemIndex?: number | null;
  clickProductRecItemName?: string | null;
  clickRedirectSuccessLocation?: string | null;
  clickRedirectSuccessName?: string | null;
  clickSubType?: string | null;
  clickSurveyComment?: string | null;
  clickSurveyRating?: number | null;
  clickType?: string | null;
  clientBrowserFingerprint?: string | null;
  clientBrowserLanguage?: string | null;
  clientBrowserName?: string | null;
  clientBrowserVersion?: string | null;
  clientCity?: string | null;
  clientCountry?: string | null;
  clientDeviceMemGb?: number | null;
  clientDeviceModel?: string | null;
  clientDeviceNetSpeedMbps?: number | null;
  clientDeviceNetType?: string | null;
  clientDeviceNumProcessors?: number | null;
  clientDeviceOsName?: string | null;
  clientDeviceOsVersion?: string | null;
  clientDeviceSizePx?: string | null;
  clientDeviceTouchpoints?: number | null;
  clientDeviceType?: string | null;
  clientDeviceVendor?: string | null;
  clientLat?: string | null;
  clientLong?: string | null;
  clientRegion?: string | null;
  config?: Config[] | null;
  customerId?: string | null;
  deviceId?: string | null;
  displayedEddBegin?: string | null;
  displayedEddEnd?: string | null;
  eddTimeRangeBegin?: string | null;
  eddTimeRangeEnd?: string | null;
  eddDisplayedTimeRange?: string | null;
  displayedLocations?: string[] | null;
  eventCarrierMoniker?: string | null;
  eventComponent?: string | null;
  eventFeature?: string | null;
  eventId?: string | null;
  eventItemGroup?: EventItemGroup[] | null;
  eventNotificationSignUpError?: string | null;
  eventNotificationSmsValidationError?: string | null;
  eventNotificationEmailValidationError?: string | null;
  eventTrackStatus?: string | null;
  eventTrackSubStatus?: string | null;
  eventTrackingNumber?: string | null;
  feature?: string | null;
  guidDateCreated?: string | null;
  ingestionTimestamp?: string | null;
  isFallbackView?: boolean | null;
  orderNumber?: string | null;
  pageCarrierService?: string | null;
  pageComponent?: string | null;
  pageEddFormatted?: string | null;
  pageHasConciergeSelected?: boolean | null;
  pageHasEdd?: boolean | null;
  pageHost?: string | null;
  pageLoadMs?: number | null;
  pageLocaleCountry?: string | null;
  pageLocaleLanguage?: string | null;
  pageOrderNumber?: string | null;
  pageReferrerUrl?: string | null;
  pageShipmentStatus?: string | null;
  pageTrackType?: string | null;
  pageUiLoadMs?: number | null;
  pageUrlLocale?: string | null;
  pageVersionName?: string | null;
  provider?: string | null;
  requestPage?: string | null;
  requestSource?: string | null;
  requestUuid?: string | null;
  segmentId?: string | null;
  segmentName?: string | null;
  trackEddBegin?: string | null;
  trackEddEnd?: string | null;
  trackEddSource?: string | null;
  trackLayout?: string | null;
  zipcodeLocation?: string | null;
}

export type AnalyticsPayload = AnalyticsPayloadRequired &
  AnalyticsPayloadNullable;

export type AnalyticsPayloadAutomatic =
  | 'clientBrowserFingerprint'
  | 'clientBrowserLanguage'
  | 'clientBrowserName'
  | 'clientBrowserVersion'
  | 'clientDeviceMemGb'
  | 'clientDeviceModel'
  | 'clientDeviceNetSpeedMbps'
  | 'clientDeviceNetType'
  | 'clientDeviceNumProcessors'
  | 'clientDeviceOsName'
  | 'clientDeviceOsVersion'
  | 'clientDeviceSizePx'
  | 'clientDeviceTimeOffsetMinsToUtc'
  | 'clientDeviceTouchpoints'
  | 'clientDeviceType'
  | 'clientDeviceVendor'
  | 'deviceId'
  | 'eventTs'
  | 'guid'
  | 'guidDateCreated'
  | 'pageHost'
  | 'pageReferrerUrl'
  | 'pageTitle'
  | 'pageUrl'
  | 'requestUuid';

export interface NoflakePayload {
  banner_segment_id?: string | null;
  banner_segment_name?: string | null;
  chat_client_session_id: string | null;
  click_asset_id: string | null;
  click_asset_multilink_id: string | null;
  click_asset_schd_end_date_utc: string | null;
  click_asset_schd_start_date_utc: string | null;
  click_asset_type: string | null;
  click_asset_url: string | null;
  click_category: string | null;
  click_delivery_instructions_comment: string | null;
  click_delivery_instructions_location: string | null;
  click_link_destination_url: string | null;
  click_link_label: string | null;
  click_location_id: string | null;
  click_notification_is_change_number: boolean | null;
  click_notification_is_opt_in: boolean | null;
  click_notification_is_opt_out: boolean | null;
  click_notification_marketing_opt_in_checked: boolean | null;
  click_notification_opt_in_disclaimer_text: string | null;
  click_notification_marketing_privacy_link: string | null;
  click_notification_marketing_terms_link: string | null;
  click_notification_marketing_opt_in_text: string | null;
  click_notification_is_order_level_signup: boolean | null;
  click_product_rec_item_data: string | null;
  click_product_rec_item_index: number | null;
  click_product_rec_item_name: string | null;
  click_redirect_success_location: string | null;
  click_redirect_success_name: string | null;
  click_sub_type: string | null;
  click_survey_comment: string | null;
  click_survey_rating: number | null;
  click_type: string | null;
  client_browser_fingerprint: string | null;
  client_browser_language: string | null;
  client_browser_name: string | null;
  client_browser_version: string | null;
  client_city: string | null;
  client_country: string | null;
  client_device_mem_gb: number | null;
  client_device_model: string | null;
  client_device_net_speed_mbps: number | null;
  client_device_net_type: string | null;
  client_device_num_processors: number | null;
  client_device_os_name: string | null;
  client_device_os_version: string | null;
  client_device_size_px: string | null;
  client_device_time_offset_mins_to_utc: string;
  client_device_touchpoints: number | null;
  client_device_type: string | null;
  client_device_vendor: string | null;
  client_lat: string | null;
  client_long: string | null;
  client_region: string | null;
  client_session_id: string | null;
  config: Config[] | null;
  customer_id: string | null;
  device_id: string | null;
  displayed_edd_begin: string | null;
  displayed_edd_end: string | null;
  displayed_locations: string[] | null;
  event: string | null;
  event_carrier_moniker: string | null;
  event_component: string | null;
  event_feature: string | null;
  event_id: string | null;
  event_item_group: EventItemGroup[] | null;
  event_notification_sign_up_error: string | null;
  event_notification_sms_validation_error: string | null;
  event_schema: string | null;
  event_track_status: string | null;
  event_track_sub_status: string | null;
  event_tracking_number: string | null;
  event_ts: string;
  event_type: string;
  feature: string | null;
  guid_date_created: string | null;
  guid: string;
  ingestion_timestamp: string | null;
  is_fallback_view: boolean | null;
  order_number: string | null;
  page_carrier_service: string | null;
  page_component: string | null;
  page_edd_formatted: string | null;
  page_has_concierge_selected: boolean | null;
  page_has_edd: boolean | null;
  page_host: string | null;
  page_load_ms: number | null;
  page_locale_country: string | null;
  page_locale_language: string | null;
  page_order_number: string | null;
  page_referrer_url: string | null;
  page_retailer_moniker: string;
  page_shipment_status: string | null;
  page_title: string;
  page_track_type: string | null;
  page_ui_load_ms: number | null;
  page_url_locale: string | null;
  page_url: string;
  page_version_name: string | null;
  product_name: string;
  provider: string | null;
  request_id: string | null;
  request_page: string | null;
  request_source: string | null;
  request_uuid: string | null;
  response_id: number | null;
  segment_id: string | null;
  segment_name: string | null;
  session_id: string | null;
  tag: NoflakeTag;
  track_edd_begin: string | null;
  track_edd_end: string | null;
  track_edd_source: string | null;
  track_layout: string | null;
  zipcode_location: string | null;
}

export default {};
